import {
  styled,
  Box,
  Paper,
  FormControl,
  FormLabel,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { IWithClassName } from "../../../theme/default.theme";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import MultiSelector from "../../../component/multi-selector";
import { IRootState, useAppSelector } from "../../../redux/store";
import { createSelector } from "@reduxjs/toolkit";
import { BidTypes, CampaignFormSteps, MinBid, Products } from "../../../enum/campaign";
import { product, campaignType, minTotalBudget } from "../constants";
import UserApp from "../../../component/user-app/user-app";
import {
  ICampaignSettings,
  ICampaignSettingsError,
} from "../../../interfaces/campaign";
import { IAppInfo } from "../../../interfaces/dto/app.dto";
import { IList } from "../../../interfaces/default";
import SearchApp from "../../../component/user-app/search-app";
import { Role } from "../../../enum/auth";

interface IProps extends IWithClassName {
  activeStep: CampaignFormSteps;
  form: ICampaignSettings;
  datepickerHandler: (label: string, date: Dayjs | null) => void;
  changeAppHandler: (
    label: keyof ICampaignSettings,
  ) => (value: IAppInfo) => void;
  multiSelectChangeHandler: (
    label: keyof ICampaignSettings,
  ) => (value: Array<IList>) => void;
  changeTextHandler: (
    label: keyof ICampaignSettings,
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  selectChangeHandler: (
    label: keyof ICampaignSettings,
  ) => (event: SelectChangeEvent<string> | string) => void;
  error: ICampaignSettingsError;
  userId?: string;
  setUserId: (userId: string) => void;
  isEditMode: boolean;
}

const selector = createSelector(
  (state: IRootState) => state.global,
  (state: IRootState) => state.campaign.details,
  (state: IRootState) => state.user.list,
  (state: IRootState) => state.auth,
  (global, details, users, auth) => ({
    ageGroup: {
      data: global.ageGroup.data ?? [],
      selected: details.data?.target?.ageGroups ?? [],
    },
    interest: {
      data: global.interest.data ?? [],
      selected: details.data?.target?.interests ?? [],
    },
    language: {
      data: global.language.data ?? [],
      selected: details.data?.target?.languages ?? [],
    },
    countries: global.countries.data ?? [],
    states: global.states.data ?? [],
    users: users.data ?? [],
    auth,
  }),
);

const Settings = ({
  className,
  activeStep,
  form,
  error,
  changeTextHandler,
  selectChangeHandler,
  changeAppHandler,
  datepickerHandler,
  multiSelectChangeHandler,
  setUserId,
  userId,
  isEditMode,
}: IProps) => {
  const { ageGroup, interest, language, users, auth, countries } =
    useAppSelector(selector);

    const [datePickerDisplay, setDatePickerDisplay] = useState({
      startDate: false,
      endDate: false,
    });

    useEffect(() => {
      selectChangeHandler("bidType")(BidTypes.CPI)
    }, [selectChangeHandler]);

  return (
    <>
      {activeStep === CampaignFormSteps.CampaignSettings && (
        <Box className={className}>
          <Box component={Paper} className="form-container-settings">
            <Grid
              className={auth.role === Role.ADMIN ? "split-view" : undefined}
            >
              {auth.role === Role.ADMIN && (
                <FormControl>
                  <InputLabel>User</InputLabel>
                  <Select
                    value={userId ?? ""}
                    onChange={({ target }) => setUserId(target.value as string)}
                    disabled={isEditMode}
                  >
                    {users.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl className="fullwidth-formcontrol">
                <TextField
                  value={form.name ?? ""}
                  error={!!error.name}
                  helperText={error.name}
                  InputLabelProps={{ shrink: !!form.name }}
                  disabled={!!isEditMode}
                  label="Campaign Name"
                  required
                  onChange={changeTextHandler("name")}
                />
              </FormControl>
            </Grid>
          </Box>

          <Box component={Paper} className="form-container-settings">
            <Typography variant="h6">Product</Typography>
            <FormControl className="split-view controled-width">
              <RadioGroup
                row
                className="app-type radio-cont"
                value={form.product ?? null}
                onChange={changeTextHandler("product")}
              >
                {product.map(({ label, value }) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio />}
                    label={label}
                  />
                ))}
              </RadioGroup>

              {/* <FormControl>
                <InputLabel>Bid Type</InputLabel>
                <Select
                  value={form.bidType ?? ""}
                  onChange={({ target }) =>
                    selectChangeHandler("bidType")(target.value)
                  }
                >
                  {bidTypes.map(
                    ({ label, app, value }) =>
                      app.includes(form.product) && (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ),
                  )}
                </Select>
              </FormControl> */}
            </FormControl>

            {form.product === Products.App ? (
              <SearchApp>
                <UserApp
                  selectHandler={changeAppHandler("app")}
                  value={form.app}
                  error={error.app}
                />
              </SearchApp>
            ) : (
              <FormControl>
                <TextField
                  label="Web URL"
                  onChange={changeTextHandler("webUrl")}
                  value={form.webUrl ?? ""}
                  error={!!error.webUrl}
                  helperText={error.webUrl}
                />
              </FormControl>
            )}
          </Box>

          <Box component={Paper} className="form-container-settings">
            <Typography variant="h6">Scheduling</Typography>

            <FormControl className="controled-width">
              <FormLabel required>Campaign Date</FormLabel>
              <Box className="campaign-date">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl>
                    <DatePicker
                      open={datePickerDisplay.startDate}
                      onOpen={() => setDatePickerDisplay(() => ({...datePickerDisplay, startDate: true}))}
                      slotProps={{
                        actionBar: {
                          actions: ["cancel"],
                        },
                        textField: {
                          onClick: () => setDatePickerDisplay(() => ({...datePickerDisplay, startDate: true}))
                        },
                      }}
                      onClose={() => setDatePickerDisplay(() => ({...datePickerDisplay, startDate: false}))}
                      label="Start Date"
                      onChange={(date) => datepickerHandler("startDate", date)}
                      minDate={dayjs()}
                      maxDate={form.endDate ? dayjs(form.endDate) : undefined}
                      value={form.startDate ? dayjs(form.startDate) : null}
                    />
                    <FormHelperText error={!!error.startDate}>
                      {error.startDate}
                    </FormHelperText>
                  </FormControl>
                  <FormControl>
                    <DatePicker
                      onOpen={() => setDatePickerDisplay(() => ({...datePickerDisplay, endDate: true}))}
                      open={datePickerDisplay.endDate}
                      label="End Date"
                      slotProps={{
                        actionBar: {
                          actions: ["cancel"],
                        },
                        textField: {
                          onClick: () => setDatePickerDisplay(() => ({...datePickerDisplay, endDate: true}))
                        },
                      }}
                      onClose={() => setDatePickerDisplay(() => ({...datePickerDisplay, endDate: false}))}
                      onChange={(date) => datepickerHandler("endDate", date)}
                      minDate={
                        form.startDate ? dayjs(form.startDate) : undefined
                      }
                      value={form.endDate ? dayjs(form.endDate) : null}
                    />
                    <FormHelperText error={!!error.endDate}>
                      {error.endDate}
                    </FormHelperText>
                  </FormControl>
                </LocalizationProvider>
              </Box>
            </FormControl>
          </Box>

          <Box component={Paper} className="form-container-settings">
            <Typography variant="h6">Campaign Type</Typography>

            <FormControl>
              <FormLabel required>Select campaign Type</FormLabel>
              <RadioGroup
                row
                className="campaign-type radio-cont"
                onChange={changeTextHandler("type")}
                value={form.type ?? null}
              >
                {campaignType.map(({ label, Icon, value }) => (
                  <FormControlLabel
                    labelPlacement="bottom"
                    value={value}
                    key={value}
                    control={<Radio icon={<Icon />} checkedIcon={<Icon />} />}
                    label={label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Box component={Paper} className="form-container-settings">
            <Typography variant="h6">Targeting</Typography>

            <Grid className="split-view" container>
              <Grid item>
                <FormControl className="fullwidth-formcontrol">
                  <FormLabel required>Country</FormLabel>
                  <Select
                    labelId="country-selector"
                    onChange={({ target }) =>
                      selectChangeHandler("country")(target.value)
                    }
                    error={!!error.country}
                    size="small"
                    value={form.country ?? ""}
                  >
                    {countries.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!error.country}>
                    {error.country}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {/* <Grid item>
                <FormControl>
                  <FormLabel required>States</FormLabel>
                  <MultiSelector
                    data={states}
                    selectedData={form.states ?? []}
                    onChange={multiSelectChangeHandler("states")}
                  />
                  <FormHelperText error={!!error.states}>
                    {error.states}
                  </FormHelperText>
                </FormControl>
              </Grid> */}
            </Grid>
          </Box>

          <Box component={Paper} className="form-container-settings">
            <Typography variant="h6">Audiences</Typography>

            <Grid className="controled-width audiences-container">
              <Grid item>
                <FormControl className="fullwidth-formcontrol">
                  <FormLabel>Interests</FormLabel>
                  <MultiSelector
                    data={interest.data}
                    selectedData={form.interests ?? []}
                    onChange={multiSelectChangeHandler("interests")}
                  />
                  <FormHelperText error={!!error.interests}>
                    {error.interests}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl className="fullwidth-formcontrol">
                  <FormLabel>Age</FormLabel>
                  <MultiSelector
                    data={ageGroup.data}
                    selectedData={form.ageGroups ?? []}
                    onChange={multiSelectChangeHandler("ageGroups")}
                  />
                  <FormHelperText error={!!error.ageGroups}>
                    {error.ageGroups}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl className="fullwidth-formcontrol">
                  <FormLabel>Languages</FormLabel>
                  <MultiSelector
                    data={language.data}
                    selectedData={form.languages ?? []}
                    onChange={multiSelectChangeHandler("languages")}
                  />
                  <FormHelperText error={!!error.languages}>
                    {error.languages}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box component={Paper} className="form-container-settings">
            <Typography variant="h6">Budget</Typography>
            <FormControl className="budget-row">
              <TextField
                required
                label="Total Budget"
                type="number"
                value={form.totalBudget ?? ""}
                onChange={changeTextHandler("totalBudget")}
                InputLabelProps={{ shrink: true }}
                error={!!error.totalBudget}
                helperText={error.totalBudget}
                inputProps={{
                  min: minTotalBudget,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                label="Daily Budget"
                type="number"
                value={form.dailyBudget ?? ""}
                error={!!error.dailyBudget}
                helperText={error.dailyBudget}
                onChange={changeTextHandler("dailyBudget")}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: form.totalBudget ?? 0,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                label="Bid"
                type="number"
                InputLabelProps={{ shrink: true }}
                value={form.bidAmount ?? ""}
                error={!!error.bidAmount}
                helperText={error.bidAmount}
                onChange={changeTextHandler("bidAmount")}
                inputProps={{
                  min: MinBid[form.bidType as keyof typeof MinBid],
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Box>
        </Box>
      )}
    </>
  );
};

export default styled(Settings)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  ".form-container-settings": {
    display: "grid",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    width: "inherit",
    ".fullwidth-formcontrol": {
      width: "100%",
    },
  },
  ".radio-cont": {
    gap: 10,
  },
  ".app-type": {
    ".MuiFormControlLabel-root": {
      paddingRight: theme.spacing(2),
      flex: 1,
    },
  },
  ".campaign-type": {
    ".MuiFormControlLabel-root": {
      width: 100,
    },
  },
  ".campaign-date": {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
  },
  ".split-view": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 49%)",
    gap: 10,
    width: "100%",
  },
  ".MuiFilledInput-root": {
    padding: theme.spacing(1),
    height: 48,
  },
  ".audiences-container .MuiSelect-select": {
    padding: 0,
  },
  ".controled-width": {
    maxWidth: 700,
  },
  ".budget-row": {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
    ".MuiFormControl-root": {
      width: "100%",
    },
  },
}));
